import type { BrowserInfo, ErrorContext, UserAction } from '@/types/Error.types'

/**
 * Collect browser information for error reports
 */
export function collectBrowserInfo(): BrowserInfo {
	return {
		userAgent: navigator.userAgent,
		platform: navigator.platform,
		language: navigator.language,
		screenResolution: `${window.screen.width}x${window.screen.height}`,
		viewportSize: `${window.innerWidth}x${window.innerHeight}`,
	}
}

/**
 * Collect full error context
 */
export function collectErrorContext(
	error: Error,
	componentStack: string | null,
	recentActions: UserAction[],
	user: { id: string; name: string } | null,
): ErrorContext {
	return {
		message: error.message,
		stack: error.stack,
		name: error.name,
		browserInfo: collectBrowserInfo(),
		url: window.location.href,
		timestamp: new Date().toISOString(),
		userId: user?.id ? parseInt(user.id, 10) : undefined,
		userName: user?.name,
		componentStack: componentStack ?? undefined,
		recentActions,
	}
}

/**
 * Sanitize error context to remove sensitive data before display
 */
export function sanitizeForDisplay(context: ErrorContext): ErrorContext {
	const sanitized = { ...context }

	// Remove sensitive patterns from URL
	sanitized.url = sanitized.url
		.replace(/token=[^&]+/gi, 'token=[REDACTED]')
		.replace(/key=[^&]+/gi, 'key=[REDACTED]')
		.replace(/password=[^&]+/gi, 'password=[REDACTED]')
		.replace(/session=[^&]+/gi, 'session=[REDACTED]')

	// Remove sensitive patterns from stack trace
	if (sanitized.stack) {
		sanitized.stack = sanitized.stack
			.replace(/Bearer\s+[A-Za-z0-9\-._~+/]+=*/gi, 'Bearer [REDACTED]')
			.replace(/password[=:]\s*[^\s&]+/gi, 'password=[REDACTED]')
	}

	return sanitized
}

/**
 * Create a formatted summary of the error for display
 */
export function formatErrorSummary(context: ErrorContext): string {
	const lines = [`${context.name}: ${context.message}`, `URL: ${context.url}`, `Time: ${context.timestamp}`]

	if (context.userId) {
		lines.push(`User ID: ${context.userId}`)
	}

	return lines.join('\n')
}

/**
 * Create a circular buffer for tracking recent actions
 */
export function createActionBuffer(maxSize: number = 20) {
	const actions: UserAction[] = []

	return {
		add(action: Omit<UserAction, 'timestamp'>) {
			const timestamped: UserAction = {
				...action,
				timestamp: new Date().toISOString(),
			}

			actions.push(timestamped)

			// Keep only the most recent actions
			if (actions.length > maxSize) {
				actions.shift()
			}
		},

		getAll(): UserAction[] {
			return [...actions]
		},

		clear() {
			actions.length = 0
		},
	}
}
